/**
 * Provides API to handle authentication tokens utilized for REST API calls.
 * @module AuthUtils
 */
/*
 * Copyright (c) 2023 Pegasystems Inc.
 * All rights reserved.
 */
import { updateHeaders, updateAuthInfo } from '../service_broker/fetch';
import { revokeTokens as revokeTokensApi } from '../service_broker/utils';
import PegaAuth from '../service_broker/embed/auth';

import PubSubUtils from './pubsub-utils';
import type { AuthConfig } from './types';

/**
 * this function will add the access token to the fetch request headers.
 * @param tokenInfo -- object containing token information like access token, refresh token, session index.
 *
 * @example <caption>Example for setTokens()</caption>
 * const tokenObject = {
 *      access_token: 'access_token_value',
 *      refresh_token: 'refresh_token_value',
 *      session_index: 'session_index',
 *      expires_in: 120,
 *      token_type: 'bearer'
 * };
 * Example usage - PCore.getAuthUtils().setTokens(tokenObject);
 * @function
 */
export const setTokens = (tokenInfo: { token_type: string; access_token: string; csrf?: string }) => {
  updateAuthInfo({
    tokenInfo
  });
  // csrf token is present and access_token is absent in secure cookie flow
  if (tokenInfo?.csrf && !tokenInfo?.access_token) {
    updateHeaders({
      pzCTkn: tokenInfo?.csrf
    });
  } else {
    updateHeaders({
      Authorization: `${tokenInfo?.token_type} ${tokenInfo?.access_token}`
    });
  }

  setTimeout(() => PubSubUtils.publish('updateSession', { updateStatus: 'success' }), 0);
};

/**
 * this function will return the instance of PegaAuth class containing helper functions for OAUTH2.0 registration in constellation apps.
 * @param config -- object containing clientId, clientSecret and endpoints.
 *
 * @returns instance of PegaAuth class.
 *
 * @example <caption>Example for getAuthInstance()</caption>
 * const config = {
 * clientId: 'clientId',
 * clientSecret: 'client_secret_value',
 * endPoints: {
 *      token: 'token_endpoint',
 *      revoke: 'revoke_endpoint',
 *      authorize: 'authorize_endpoint'
 *  }
 * };
 * Example usage - PCore.getAuthUtils().getAuthInstance(config);
 * @function
 */
export const getAuthInstance = (config: AuthConfig) => {
  return PegaAuth.init(config);
};

/**
 * this function will revoke the access and refresh tokens.
 * @returns Resolves when both the tokens are successfully revoked
 *
 * @example <caption>Example for revokeTokens()</caption>
 * Example usage - PCore.getAuthUtils().revokeTokens();
 * @function
 */
export const revokeTokens = async () => {
  return revokeTokensApi();
};

/**
 * this function will set the Authorization header in the fetch request headers for API calls.
 * @param value header value
 *
 * @example <caption>Example for setAuthorizationHeader()</caption>
 * Example usage - PCore.getAuthUtils().setAuthorizationHeader('header_value');
 * @function
 */

export const setAuthorizationHeader = (value: string) => {
  updateHeaders({
    Authorization: value
  });
  setTimeout(() => PubSubUtils.publish('updateSession', { updateStatus: 'success' }), 0);
};

export default {
  setTokens,
  revokeTokens,
  getAuthInstance,
  setAuthorizationHeader
};
